import {
  Class,
  CreateClassRequest,
  UpdateClassRequest,
  AddStudentToClassRequest,
  AddSubjectToClassRequest,
  AssignTeacherToSubjectRequest,
  AddPeriodRequest,
  ClassStatistics,
  ClassFilter,
  ClassWithDetails,
} from "../types/class";
import { PaginatedResponse, ApiResponse } from "../types/common";
import api from "./api";

// API Base URL for fallback functions that still use direct axios
const API_BASE = `${
  import.meta.env.VITE_BACKEND_URL || "http://localhost:5000"
}/api`;

// Helper function for API requests (for backward compatibility)
const apiRequest = async (method: string, url: string, data?: any) => {
  let response;
  switch (method.toUpperCase()) {
    case "GET":
      response = await api.get(url.replace(API_BASE, ""));
      break;
    case "POST":
      response = await api.post(url.replace(API_BASE, ""), data);
      break;
    case "PUT":
      response = await api.put(url.replace(API_BASE, ""), data);
      break;
    case "DELETE":
      response = await api.delete(url.replace(API_BASE, ""));
      break;
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
  return response.data || response;
};

// Get all classes with filtering and pagination
export const getClasses = async (
  filters: ClassFilter = {}
): Promise<PaginatedResponse<ClassWithDetails>> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  const queryString = params.toString();
  const url = queryString ? `/classes?${queryString}` : "/classes";

  const response = await api.get(url);

  // Transform API response structure from backend format to frontend format
  if (response && (response as any).classes) {
    return {
      success: (response as any).success,
      data: (response as any).classes,
      pagination: {
        currentPage: (response as any).currentPage,
        totalPages: (response as any).totalPages,
        totalItems: (response as any).total,
        hasNext: (response as any).currentPage < (response as any).totalPages,
        hasPrev: (response as any).currentPage > 1,
      },
    };
  }

  return response as unknown as PaginatedResponse<ClassWithDetails>;
};

// Get classes for the current teacher
export const getTeacherClasses = async (): Promise<ApiResponse<Class[]>> => {
  return api.get("/teacher/classes");
};

// Get class statistics
export const getClassStatistics = async (): Promise<{
  success: boolean;
  data: ClassStatistics;
}> => {
  const response = await api.get("/classes/statistics");
  return response.data || response;
};

// Get classes by academic term
export const getClassesByTerm = async (
  termId: string
): Promise<{ success: boolean; data: Class[] }> => {
  const response = await api.get(`/classes/term/${termId}`);
  return response.data || response;
};

// Create a new class
export const createClass = async (
  data: CreateClassRequest
): Promise<ApiResponse<Class>> => {
  try {
    const response = await api.post("/classes", data);
    return {
      success: (response as any).success,
      data: (response as any).class || (response as any).data,
      message: (response as any).message,
    };
  } catch (error: any) {
    console.error("Error creating class:", error);
    throw error; // Re-throw to handle in component
  }
};

// Get a specific class by ID
export const getClass = async (
  id: string
): Promise<{ success: boolean; class: Class }> => {
  const response = await api.get(`/classes/${id}`);
  return response.data || response;
};

// Update a class
export const updateClass = async (
  id: string,
  data: UpdateClassRequest
): Promise<ApiResponse<Class>> => {
  try {
    const response = await api.put(`/classes/${id}`, data);
    return {
      success: (response as any).success,
      data: (response as any).class || (response as any).data,
      message: (response as any).message,
    };
  } catch (error: any) {
    console.error("Error updating class:", error);
    throw error; // Re-throw to handle in component
  }
};

// Delete a class
export const deleteClass = async (
  id: string
): Promise<{ success: boolean; message: string }> => {
  return apiRequest("DELETE", `${API_BASE}/classes/${id}`);
};

// Add a student to a class
export const addStudentToClass = async (
  classId: string,
  data: AddStudentToClassRequest
): Promise<{ success: boolean; message: string }> => {
  return apiRequest("POST", `${API_BASE}/classes/${classId}/students`, data);
};

// Remove a student from a class
export const removeStudentFromClass = async (
  classId: string,
  studentId: string
): Promise<{ success: boolean; message: string }> => {
  return apiRequest(
    "DELETE",
    `${API_BASE}/classes/${classId}/students/${studentId}`
  );
};

// Add a subject to a class
export const addSubjectToClass = async (
  classId: string,
  data: AddSubjectToClassRequest
): Promise<{ success: boolean; message: string }> => {
  return apiRequest("POST", `${API_BASE}/classes/${classId}/subjects`, data);
};

// Assign a teacher to a subject in a class
export const assignTeacherToSubject = async (
  classId: string,
  subjectName: string,
  data: AssignTeacherToSubjectRequest
): Promise<{ success: boolean; message: string }> => {
  // URL encode the subject name to handle spaces and special characters
  const encodedSubjectName = encodeURIComponent(subjectName);

  return apiRequest(
    "POST",
    `${API_BASE}/classes/${classId}/subjects/${encodedSubjectName}/assign-teacher`,
    data
  );
};

// Remove a teacher from a subject in a class
export const removeTeacherFromSubject = async (
  classId: string,
  subjectName: string,
  teacherId: string
): Promise<{ success: boolean; message: string }> => {
  // URL encode the subject name to handle spaces and special characters
  const encodedSubjectName = encodeURIComponent(subjectName);

  return apiRequest(
    "DELETE",
    `${API_BASE}/classes/${classId}/subjects/${encodedSubjectName}/remove-teacher/${teacherId}`
  );
};

// Set class teacher
export const setClassTeacher = async (
  classId: string,
  teacherId?: string
): Promise<{ success: boolean; message: string }> => {
  return apiRequest("PUT", `${API_BASE}/classes/${classId}/class-teacher`, {
    teacherId,
  });
};

// Add a period to class schedule
export const addPeriodToSchedule = async (
  classId: string,
  data: AddPeriodRequest
): Promise<{ success: boolean; message: string }> => {
  return apiRequest(
    "POST",
    `${API_BASE}/classes/${classId}/schedule/periods`,
    data
  );
};

// Update a period in class schedule
export const updatePeriodInSchedule = async (
  classId: string,
  periodIndex: number,
  data: AddPeriodRequest
): Promise<{ success: boolean; message: string }> => {
  return apiRequest(
    "PUT",
    `${API_BASE}/classes/${classId}/schedule/periods/${periodIndex}`,
    data
  );
};

// Delete a period from class schedule
export const deletePeriodFromSchedule = async (
  classId: string,
  periodIndex: number
): Promise<{ success: boolean; message: string }> => {
  return apiRequest(
    "DELETE",
    `${API_BASE}/classes/${classId}/schedule/periods/${periodIndex}`
  );
};

// Get class schedule
export const getClassSchedule = async (
  classId: string
): Promise<{
  success: boolean;
  data: {
    classId: string;
    schedule: any[];
  };
}> => {
  return apiRequest("GET", `${API_BASE}/classes/${classId}/schedule`);
};

// Bulk operations
export const bulkAssignStudents = async (
  classId: string,
  studentIds: string[]
): Promise<{
  success: boolean;
  data: {
    successful: number;
    failed: number;
    errors: string[];
  };
}> => {
  return apiRequest("POST", `${API_BASE}/classes/${classId}/students/bulk`, {
    studentIds,
  });
};

export const bulkRemoveStudents = async (
  classId: string,
  studentIds: string[]
): Promise<{
  success: boolean;
  data: {
    successful: number;
    failed: number;
    errors: string[];
  };
}> => {
  return apiRequest("DELETE", `${API_BASE}/classes/${classId}/students/bulk`, {
    studentIds,
  });
};
